import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Video, Link2, Upload, Trash2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 50 * 1024 * 1024;

/**
 * Welcome clip control. `scope` is "mlo" (an officer's own clip) or "house"
 * (the admin fallback used when an officer hasn't recorded one).
 */
export const WelcomeVideoCard = ({ scope = "mlo", authHeader }) => {
  const isHouse = scope === "house";
  const prefix = isHouse ? "/admin" : "/mlo";
  const initPath = isHouse ? "/admin/uploads/video/init" : "/uploads/video/init";

  const [video, setVideo] = useState(null);
  const [effective, setEffective] = useState(null);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}${prefix}/welcome-video`, { headers: authHeader() });
      setVideo(data.own);
      setEffective(data.effective);
    } catch {
      setVideo(null);
    } finally {
      setLoading(false);
    }
  }, [prefix, authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const saveLink = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("url", url.trim());
      const { data } = await axios.put(`${API}${prefix}/welcome-video`, body, { headers: authHeader() });
      setVideo(data.video);
      setEffective(data.video);
      setUrl("");
      toast.success("Welcome clip saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that link.");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("That clip is over 50MB. Trim it, or paste a hosted link instead.");
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const initBody = new FormData();
      initBody.append("filename", file.name);
      initBody.append("content_type", file.type);
      initBody.append("size", file.size);
      const { data: init } = await axios.post(`${API}${initPath}`, initBody, { headers: authHeader() });

      const size = init.chunk_size;
      const total = Math.ceil(file.size / size);
      for (let i = 0; i < total; i += 1) {
        const body = new FormData();
        body.append("index", i);
        body.append("chunk", file.slice(i * size, (i + 1) * size));
        await axios.post(`${API}/uploads/video/${init.upload_id}/chunk`, body, { headers: authHeader() });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      const { data } = await axios.post(
        `${API}${prefix}/welcome-video/upload/${init.upload_id}`,
        {},
        { headers: authHeader() },
      );
      setVideo(data.video);
      setEffective(data.video);
      toast.success("Welcome clip uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That upload didn't finish. Try again.");
    } finally {
      setBusy(false);
      setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clear = async () => {
    if (!window.confirm("Remove the current welcome clip?")) return;
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}${prefix}/welcome-video`, { headers: authHeader() });
      setVideo(null);
      setEffective(data.video);
      toast.success("Clip removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove that.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6 flex items-center gap-2 text-sm text-ink-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your welcome clip…
      </div>
    );
  }

  const current = video;

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6" data-testid={`welcome-video-card-${scope}`}>
      <div className="flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-teal-light text-teal">
          <Video size={17} />
        </span>
        <div>
          <h3 className="font-heading font-medium text-lg text-navy">
            {isHouse ? "House welcome clip" : "Your welcome clip"}
          </h3>
          <p className="text-xs text-ink-500">
            {isHouse
              ? "Plays for approved partners whose officer hasn't recorded one of their own."
              : "Plays at the top of your partners' first-week page. Sixty seconds in your own voice beats any brochure."}
          </p>
        </div>
      </div>

      {current ? (
        <div className="mt-5 rounded-2xl overflow-hidden border border-linen-border bg-navy" data-testid={`welcome-video-preview-${scope}`}>
          {current.kind === "file" ? (
            <video src={`${process.env.REACT_APP_BACKEND_URL}${current.url}`} controls className="w-full max-h-72 bg-black" />
          ) : (
            <iframe
              src={current.embed}
              title="Welcome clip"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
              allowFullScreen
              className="w-full aspect-video"
            />
          )}
        </div>
      ) : (
        <p className="mt-5 rounded-xl bg-linen/60 border border-linen-border p-4 text-sm text-ink-500">
          {isHouse
            ? "No house clip set. Officers without their own clip will simply see no video."
            : "No clip yet — your partners currently see the house welcome, if there is one."}
        </p>
      )}

      {!isHouse && effective && !video && (
        <p className="mt-3 text-[11px] text-ink-400" data-testid="welcome-video-fallback-note">
          Currently showing the house clip from {effective.from_name || "Great Rate Check"}.
        </p>
      )}

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2.5">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-500">Paste a link</span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Loom, YouTube or Vimeo link"
            data-testid={`welcome-video-url-${scope}`}
            className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
          />
        </label>
        <button
          onClick={saveLink}
          disabled={busy || !url.trim()}
          data-testid={`welcome-video-save-link-${scope}`}
          className="sm:self-end inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
        >
          {busy && progress === null ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} Use link
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          ref={fileRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`welcome-video-file-${scope}`}
          className="hidden"
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid={`welcome-video-upload-${scope}`}
          className="inline-flex items-center gap-2 bg-white border border-linen-border hover:border-teal text-navy font-semibold text-sm px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
        >
          <Upload size={15} /> Upload a file
        </button>
        {current && (
          <button
            onClick={clear}
            disabled={busy}
            data-testid={`welcome-video-clear-${scope}`}
            className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-red-600 transition-colors"
          >
            <Trash2 size={14} /> Remove
          </button>
        )}
        <span className="text-[11px] text-ink-400">MP4, MOV or WebM · up to 50MB</span>
      </div>

      {progress !== null && (
        <div className="mt-4" data-testid={`welcome-video-progress-${scope}`}>
          <div className="h-2 rounded-full bg-linen overflow-hidden">
            <div className="h-full bg-teal transition-all" style={{ width: `${progress}%` }} />
          </div>
          <p className="mt-1.5 text-[11px] text-ink-500 flex items-center gap-1">
            {progress === 100 ? <Check size={12} className="text-teal" /> : null}
            {progress === 100 ? "Finishing up…" : `Uploading ${progress}%`}
          </p>
        </div>
      )}
    </div>
  );
};

export default WelcomeVideoCard;
